import { useState } from 'react';
import { useAlerts, useAlertStats, useAcknowledgeAlert, useAcknowledgeAllAlerts } from '@/hooks/useAlerts';
import { getAlertId } from '@/api/alerts.api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  AlertTriangle,
  Bell,
  BellOff,
  AlertCircle,
  Info,
  XCircle,
  Filter,
  Clock,
  User,
  GitBranch,
  CheckCircle,
  ChevronRight,
} from 'lucide-react';
import { formatRelativeTime } from '@/utils/helpers';
import { Alert, AlertSeverity } from '@/types';

/**
 * Severity configuration
 */
const severityConfig: Record<AlertSeverity, { icon: typeof AlertTriangle; color: string; bg: string }> = {
  critical: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100' },
  high: { icon: AlertTriangle, color: 'text-orange-600', bg: 'bg-orange-100' },
  medium: { icon: AlertCircle, color: 'text-yellow-600', bg: 'bg-yellow-100' },
  low: { icon: Info, color: 'text-blue-600', bg: 'bg-blue-100' },
  info: { icon: Info, color: 'text-gray-600', bg: 'bg-gray-100' },
};

type FilterType = 'all' | 'unacknowledged' | AlertSeverity;

/**
 * Alert card component
 */
function AlertCard({ alert, onAcknowledge }: { alert: Alert; onAcknowledge: (id: string) => void }) {
  const config = severityConfig[alert.severity] || severityConfig.info;
  const Icon = config.icon;
  const alertId = getAlertId(alert);

  return (
    <Card className={`hover:border-primary/50 transition-colors ${!alert.acknowledged ? 'border-l-4 border-l-' + alert.severity : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className={`p-2 rounded ${config.bg}`}>
            <Icon className={`h-5 w-5 ${config.color}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-medium">{alert.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">{alert.message}</p>
              </div>
              {!alert.acknowledged && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!alertId}
                  onClick={() => alertId && onAcknowledge(alertId)}
                >
                  Acknowledge
                </Button>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-muted-foreground">
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.color}`}>
                {alert.severity}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatRelativeTime(alert.createdAt)}
              </span>
              {alert.userId && typeof alert.userId === 'object' && (
                <span className="flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {alert.userId.displayName || alert.userId.username}
                </span>
              )}
              {alert.repoId && typeof alert.repoId === 'object' && (
                <span className="flex items-center gap-1">
                  <GitBranch className="h-3 w-3" />
                  {alert.repoId.name}
                </span>
              )}
              {alert.acknowledged && (
                <span className="flex items-center gap-1 text-green-600">
                  <CheckCircle className="h-3 w-3" />
                  Acknowledged
                </span>
              )}
              {alert.resolved && (
                <span className="flex items-center gap-1 text-blue-600">
                  <CheckCircle className="h-3 w-3" />
                  Resolved
                </span>
              )}
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
        </div>
      </CardContent>
    </Card>
  );
}

export default function Alerts() {
  const [filter, setFilter] = useState<FilterType>('all');
  const [page, setPage] = useState(1);

  // Fetch alerts
  const { data: alertsData, isLoading } = useAlerts({
    page,
    limit: 20,
    severity: filter !== 'all' && filter !== 'unacknowledged' ? filter : undefined,
    acknowledged: filter === 'unacknowledged' ? false : undefined,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });
  const totalPages = alertsData?.pagination?.totalPages ?? 0;

  // Fetch stats (all-time, same scope as the unfiltered list)
  const { data: stats } = useAlertStats();

  // Mutations
  const acknowledgeMutation = useAcknowledgeAlert();
  const acknowledgeAllMutation = useAcknowledgeAllAlerts();

  const handleAcknowledge = async (id: string) => {
    if (!id) return;
    await acknowledgeMutation.mutateAsync(id);
  };

  // The button shows the all-time unacknowledged count, so acknowledge all of
  // them (not just the alerts on the current page)
  const handleAcknowledgeAll = async () => {
    if (!stats?.unacknowledged) return;
    await acknowledgeAllMutation.mutateAsync();
  };

  const filterOptions: { value: FilterType; label: string }[] = [
    { value: 'all', label: 'All Alerts' },
    { value: 'unacknowledged', label: 'Unacknowledged' },
    { value: 'critical', label: 'Critical' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Alerts</h1>
          <p className="text-muted-foreground">
            Monitor system alerts and notifications
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleAcknowledgeAll}
          disabled={acknowledgeAllMutation.isPending || !stats?.unacknowledged}
        >
          {acknowledgeAllMutation.isPending ? (
            <LoadingSpinner className="h-4 w-4 mr-2" />
          ) : (
            <BellOff className="h-4 w-4 mr-2" />
          )}
          Acknowledge All ({stats?.unacknowledged || 0})
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Alerts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <XCircle className="h-4 w-4 text-red-600" />
              Critical
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats?.bySeverity?.critical || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
              High
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{stats?.bySeverity?.high || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              Medium
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{stats?.bySeverity?.medium || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Unacknowledged</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.unacknowledged || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <div className="flex gap-2 flex-wrap">
          {filterOptions.map((option) => (
            <Button
              key={option.value}
              variant={filter === option.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setFilter(option.value);
                setPage(1);
              }}
            >
              {option.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Alert List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : !alertsData?.data?.length ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Bell className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No alerts</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              {filter !== 'all'
                ? 'No alerts match the selected filter'
                : 'Alerts will appear here when the system detects issues such as:'}
            </p>
            {filter === 'all' && (
              <div className="mt-6 grid gap-3 md:grid-cols-2 max-w-lg mx-auto text-left">
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  <User className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium text-sm">Inactivity Alerts</p>
                    <p className="text-xs text-muted-foreground">Users with no commits</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  <GitBranch className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium text-sm">Code Quality Issues</p>
                    <p className="text-xs text-muted-foreground">Low scores from AI reviews</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium text-sm">Budget Warnings</p>
                    <p className="text-xs text-muted-foreground">AI cost approaching limits</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {alertsData.data.map((alert, index) => (
            <AlertCard key={getAlertId(alert) || `alert-${index}`} alert={alert} onAcknowledge={handleAcknowledge} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span className="flex items-center px-4 text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Alert Legend */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Alert Severity Levels</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-5">
            {Object.entries(severityConfig).map(([severity, config]) => {
              const Icon = config.icon;
              return (
                <div key={severity} className="flex items-center gap-2">
                  <div className={`p-1.5 rounded ${config.bg}`}>
                    <Icon className={`h-4 w-4 ${config.color}`} />
                  </div>
                  <span className="text-sm capitalize">{severity}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
